<script setup>
const { application, customDocuments } = defineProps({
  application: Object,
  customDocuments: Array,
});

const toast = useToast();
const visible = ref(false);

const inputFile = ref();
const inputText = ref("");

const fileUploadError = ref("");

const newDocument = ref({
  label: "",
  input_type: "text",
  input_value: "",
});

const inputTypeOptions = [
  {
    label: "Add Website Link",
    value: "text",
  },
  {
    label: "Upload File",
    value: "file",
  },
];

const isDisabled = computed(() => {
  return (
    newDocument.value.label === "" ||
    (newDocument.value.input_type === "text"
      ? inputText.value === ""
      : !inputFile.value)
  );
});

const onFileSelect = async (event) => {
  const file = event.files[0];

  fileUploadError.value = "";
  if (file.size > MAX_FILE_UPLOAD_SIZE) {
    fileUploadError.value = `${file.name} file too large. Please upload a file under 2MB.`;
    return;
  }
  inputFile.value = file;
};

const onFormSubmit = async () => {
  visible.value = false;
  const formData = new FormData();

  let label = newDocument.value.label;
  const existingLabels = customDocuments.map((doc) => doc.label);
  let counter = 2;

  while (existingLabels.includes(label)) {
    label = `${newDocument.value.label} ${counter}`;
    counter++;
  }

  formData.append("label", label);
  formData.append("input_type", newDocument.value.input_type);

  if (newDocument.value.input_type === "text") {
    formData.append("input_value", inputText.value);
  } else {
    formData.append("input_value", inputFile.value);
  }

  await useApiFetch(`/student-applications/${application.id}/documents`, {
    method: "POST",
    body: formData,
    onResponse() {
      toast.add({
        severity: "success",
        summary: "Document is submitted.",
        life: 3000,
      });
      inputText.value = null;
      inputFile.value = null;
      newDocument.value = {
        label: "",
        input_type: "text",
        input_value: "",
      };
      refreshNuxtData("student-application-detail");
    },
  });
};
</script>
<template>
  <Button
    v-if="application.status == 'consulted'"
    label="Add new"
    icon="pi pi-plus"
    class="text-sm"
    text
    aria-haspopup="true"
    aria-controls="overlay_menu"
    @click="visible = true"
  />

  <Dialog
    v-model:visible="visible"
    modal
    header="Add new document"
    :style="{ width: '30rem' }"
  >
    <form @submit.prevent="onFormSubmit" class="flex flex-col gap-4 w-full">
      <div>
        <Label for="label" isRequired class="text-muted">Document Title</Label>
        <InputText
          placeholder="e.g., Personal Statement, Portfolio"
          id="label"
          v-model="newDocument.label"
          class="w-full"
          size="small"
        />
      </div>
      <div>
        <Label for="inputType" isRequired class="text-muted"
          >How would you like to upload? (Link/File)</Label
        >
        <Select
          v-model="newDocument.input_type"
          :options="inputTypeOptions"
          optionLabel="label"
          optionValue="value"
          class="w-full"
          size="small"
        />
      </div>
      <div class="space-y-2">
        <Label
          for="value"
          v-if="newDocument.input_type === 'text'"
          isRequired
          class="text-muted capitalize"
          >Enter Website Link</Label
        >
        <Label v-else>Upload File </Label>
        <InputText
          v-if="newDocument.input_type === 'text'"
          id="value"
          placeholder="https://myportfolio.com/"
          v-model="inputText"
          class="w-full"
        />

        <div
          v-else
          class="flex justify-start items-center gap-2 p-1.5 pr-3 rounded-[3px] border border-[#BAC7D5] bg-white"
        >
          <FileUpload
            ref="fileupload"
            mode="basic"
            choose-icon="pi pi-upload"
            accept="image/jpeg, image/png, image/jpg, application/pdf"
            @select="onFileSelect"
            customUpload
            severity="secondary"
            auto
            :pt="{
              root: {
                class: 'flex flex-nowrap text-sm items-center gap-2 truncate',
              },
              pcButton: {
                label: 'Select file',
                severity: 'secondary',
                size: 'small',
                class:
                  'shrink-0 !text-[#252A31] !rounded-[3px] !bg-surface-100',
              },
            }"
          />
          <p class="text-sm text-[#252A31] font-medium">
            {{ inputFile?.name }}
          </p>
        </div>
        <p class="text-sm text-rose-600 font-medium">
          {{ fileUploadError }}
        </p>
      </div>
      <Button
        type="submit"
        label="Submit"
        class="mt-4"
        :disabled="isDisabled"
      />
    </form>
  </Dialog>
</template>
